import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  dirPickerSupported, pickDir, getAllDirNames, clearDir, getHandles, getAccessStates,
  requestAccessAll, pickInboxDir, getInboxDirName, clearInboxDir, getInboxHandle,
  getInboxAccess, requestInboxAccess, type TargetKind, type AccessState,
} from '@/lib/fileTargets';

/**
 * Welle 12 (pre128): Wiederverwendbare Ordner-Konfiguration (File System Access API).
 * Setzt je Dokumententyp ein Zielverzeichnis (direkte Ablage ohne Dialog) sowie den
 * Eingangsordner für die Unterschrift. Wird im Companion verwendet, damit dort die
 * gleichen Ablageziele wie im normalen Client konfiguriert werden können.
 */
const KIND_DIRS: { kind: TargetKind; label: string }[] = [
  { kind: 'scan', label: 'Scans' },
  { kind: 'signed', label: 'Unterschriebene Dokumente' },
  { kind: 'nfc', label: 'EC-Karten-Protokolle' },
];

export function FolderTargetsConfig() {
  const supported = dirPickerSupported();
  const [dirNames, setDirNames] = useState<Record<TargetKind, string | null>>({ scan: null, signed: null, nfc: null });
  const [access, setAccess] = useState<Record<TargetKind, AccessState>>({ scan: 'none', signed: 'none', nfc: 'none' });
  const handlesRef = useRef<Record<TargetKind, any | null>>({ scan: null, signed: null, nfc: null });

  const [inboxName, setInboxName] = useState<string | null>(null);
  const [inboxAccess, setInboxAccess] = useState<AccessState>('none');
  const inboxHandleRef = useRef<any | null>(null);

  async function refreshTargets() {
    const [names, handles] = await Promise.all([getAllDirNames(), getHandles()]);
    handlesRef.current = handles;
    setDirNames(names);
    setAccess(await getAccessStates(handles));
  }
  async function refreshInbox() {
    inboxHandleRef.current = await getInboxHandle();
    setInboxName(await getInboxDirName());
    setInboxAccess(await getInboxAccess(inboxHandleRef.current));
  }
  useEffect(() => { if (supported) { refreshTargets().catch(() => {}); refreshInbox().catch(() => {}); } }, [supported]);

  async function chooseDir(kind: TargetKind) {
    try { const name = await pickDir(kind); if (name) await refreshTargets(); } catch { /* abgebrochen */ }
  }
  async function removeDir(kind: TargetKind) { await clearDir(kind).catch(() => {}); await refreshTargets(); }

  function confirmAccess() {
    requestAccessAll(handlesRef.current).then(() => getAccessStates(handlesRef.current)).then(setAccess).catch(() => {});
    if (inboxHandleRef.current) requestInboxAccess(inboxHandleRef.current).then(() => refreshInbox()).catch(() => {});
  }
  const needsConfirm = (['scan', 'signed', 'nfc'] as TargetKind[]).some((k) => dirNames[k] && access[k] !== 'granted')
    || (!!inboxName && inboxAccess !== 'granted');

  if (!supported) {
    return (
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
        Direkte Ordner-Ablage wird von diesem Browser nicht unterstützt. Dateien werden über „Speichern unter…" abgelegt.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {KIND_DIRS.map(({ kind, label }) => (
        <div key={kind} style={dirBar}>
          <span style={tag}>{label}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dirNames[kind] ? <strong style={{ color: 'var(--fg)' }}>{dirNames[kind]}</strong> : <span style={{ color: 'var(--muted)' }}>nicht gewählt</span>}
          </span>
          {dirNames[kind] && (
            access[kind] === 'granted'
              ? <span style={dotOk} title="Zugriff aktiv">● aktiv</span>
              : <span style={dotWarn} title="Zugriff muss bestätigt werden">● zu bestätigen</span>
          )}
          <button onClick={() => chooseDir(kind)} style={miniBtn}>{dirNames[kind] ? 'Ändern' : 'Ordner wählen'}</button>
          {dirNames[kind] && <button onClick={() => removeDir(kind)} style={{ ...miniBtn, color: '#DC2626' }}>Entfernen</button>}
        </div>
      ))}

      <div style={dirBar}>
        <span style={{ ...tag, background: '#6d28d9' }}>Eingang</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {inboxName ? <strong style={{ color: 'var(--fg)' }}>{inboxName}</strong> : <span style={{ color: 'var(--muted)' }}>nicht gewählt</span>}
        </span>
        {inboxName && (
          inboxAccess === 'granted'
            ? <span style={dotOk} title="Zugriff aktiv">● aktiv</span>
            : <span style={dotWarn} title="Zugriff muss bestätigt werden">● zu bestätigen</span>
        )}
        <button onClick={async () => { try { const n = await pickInboxDir(); if (n) await refreshInbox(); } catch { /* abgebrochen */ } }} style={miniBtn}>{inboxName ? 'Ändern' : 'Ordner wählen'}</button>
        {inboxName && <button onClick={async () => { await clearInboxDir(); await refreshInbox(); }} style={{ ...miniBtn, color: '#DC2626' }}>Entfernen</button>}
      </div>

      {needsConfirm && (
        <button onClick={confirmAccess} style={{ ...miniBtn, alignSelf: 'flex-start', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderColor: 'transparent', fontWeight: 700 }}>
          Ordner-Zugriff bestätigen
        </button>
      )}
      <div style={{ fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.45 }}>
        Der Browser merkt sich die Ordner; nach einem Neustart muss der Zugriff einmal bestätigt werden.
      </div>
    </div>
  );
}

const dirBar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const tag: CSSProperties = { fontSize: 10.5, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-elev-3, #2a2a2a)', color: 'var(--fg)', flexShrink: 0, whiteSpace: 'nowrap' };
const miniBtn: CSSProperties = { fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 6, border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--fg)', cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' };
const dotOk: CSSProperties = { fontSize: 10.5, color: 'var(--status-available, #22C55E)', fontWeight: 700, flexShrink: 0 };
const dotWarn: CSSProperties = { fontSize: 10.5, color: '#d8a23a', fontWeight: 700, flexShrink: 0 };
